import AsyncStorage from "@react-native-async-storage/async-storage";
import { useRouter } from "expo-router";
import { ClipboardCheck, FileText, Gift, MessageCircle, ShieldCheck } from "../../../components/lucide-icons";
import { useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { Badge, Button, ErrorState, LoadingState, Panel, PermissionState, Status } from "../../../components/ui";
import { palette, semantic, spacing } from "../../../theme/tokens";
import {
  ArrangementProgress,
  DemoSection,
  Fact,
  MonarchDemoShell,
  NextActionBar,
} from "../components/arrangement-demo-components";
import { demoArrangementCase } from "../data/demo-arrangement-case";
import { arrangementRoutes } from "../routes";
import {
  FAMILY_SUBMISSION_STORAGE_KEY,
  FamilySubmissionRecord,
  parseFamilySubmissionRecord,
} from "../utils/family-critical-flow";
import { formatDecedentName } from "../utils/format-arrangement";

export default function ArrangementConfirmationScreen() {
  const router = useRouter();
  const [submissionState, setSubmissionState] = useState<
    "loading" | "submitted" | "queued_offline" | "missing" | "error"
  >("loading");
  const [submission, setSubmission] = useState<FamilySubmissionRecord | null>(null);

  useEffect(() => {
    let active = true;
    AsyncStorage.getItem(FAMILY_SUBMISSION_STORAGE_KEY)
      .then((raw) => {
        if (!active) return;
        if (!raw) {
          setSubmissionState("missing");
          return;
        }
        const parsed = parseFamilySubmissionRecord(raw);
        if (!parsed) throw new Error("Missing submission record");
        setSubmission(parsed);
        setSubmissionState(parsed.status);
      })
      .catch(() => {
        if (active) setSubmissionState("error");
      });
    return () => {
      active = false;
    };
  }, []);

  if (submissionState === "loading") {
    return (
      <MonarchDemoShell activeLabel="Submission confirmation">
        <LoadingState label="Confirming your request" description="Checking the private submission record on this device." />
      </MonarchDemoShell>
    );
  }

  if (submissionState === "missing") {
    return (
      <MonarchDemoShell activeLabel="Submission confirmation">
        <PermissionState
          title="No submitted arrangement was found"
          description="Complete the family review and send the request before opening confirmation."
          action={{ label: "Return to arrangement", onPress: () => router.replace(arrangementRoutes.studio as never) }}
        />
      </MonarchDemoShell>
    );
  }

  if (submissionState === "queued_offline") {
    return (
      <MonarchDemoShell activeLabel="Submission waiting">
        <ErrorState
          title="Your request is saved, but not sent"
          description="Reconnect to the internet, return to the arrangement review, and retry. Your answers remain on this device."
          action={{ label: "Retry from review", onPress: () => router.replace(arrangementRoutes.studio as never) }}
        />
      </MonarchDemoShell>
    );
  }

  if (submissionState === "error") {
    return (
      <MonarchDemoShell activeLabel="Submission confirmation">
        <ErrorState
          title="We couldn’t verify this confirmation"
          description="The saved submission record could not be read safely. Return to the arrangement review to verify or resend it."
          action={{ label: "Return to review", onPress: () => router.replace(arrangementRoutes.studio as never) }}
        />
      </MonarchDemoShell>
    );
  }

  return (
    <MonarchDemoShell activeLabel="Submitted to Monarch">
      <DemoSection
        label="Submitted"
        title="Your request has been sent to Monarch."
        body="A licensed funeral director will review the details, confirm pricing and disclosures, and follow up with the next step."
      >
        <ArrangementProgress currentIndex={4} />
      </DemoSection>

      <Panel spotlight style={styles.confirmPanel}>
        <View style={styles.confirmIcon}>
          <ClipboardCheck color={palette.gold} size={36} strokeWidth={1.6} />
        </View>
        <View style={styles.confirmCopy}>
          <Badge label="Director review required" tone="forest" />
          <Status label="Submission received on this device" tone="success" />
          <Text style={styles.confirmTitle}>{formatDecedentName(demoArrangementCase.decedent)} arrangement request</Text>
          <Text style={styles.confirmBody}>
            Monarch has the service preferences, merchandise selections, digital folder draft,
            guestbook settings, keepsake requests, and missing information checklist. A licensed
            funeral director will confirm pricing, availability, and required disclosures.
          </Text>
        </View>
      </Panel>

      <View style={styles.grid}>
        <Fact icon={ShieldCheck} label="Case status" value="Needs director review" />
        <Fact
          icon={ClipboardCheck}
          label="Submitted"
          value={submission?.submittedAt ? new Date(submission.submittedAt).toLocaleString() : "Recorded"}
        />
        <Fact icon={FileText} label="Digital folder" value="Draft ready · QR link available" />
        <Fact icon={MessageCircle} label="Guestbook" value="Messages, photos, private notes enabled" />
        <Fact icon={Gift} label="Keepsakes" value="2 requests · pricing confirmation needed" />
      </View>

      <Panel style={styles.nextPanel}>
        <View style={styles.nextCopy}>
          <Text style={styles.nextTitle}>What happens next</Text>
          <Text style={styles.nextBody}>
            Monarch now has a structured case for paperwork review, missing items,
            statement preparation, service-folder setup, and family follow-up.
          </Text>
        </View>
        <NextActionBar>
          <Button label="Review Arrangement" onPress={() => router.push(arrangementRoutes.studio as never)} />
          <Button
            label="Return to Monarch"
            variant="secondary"
            onPress={() => router.push(arrangementRoutes.start as never)}
          />
        </NextActionBar>
      </Panel>
    </MonarchDemoShell>
  );
}

const styles = StyleSheet.create({
  confirmPanel: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.lg,
  },
  confirmIcon: {
    width: 92,
    height: 92,
    borderRadius: 46,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: semantic.overlay.gold,
  },
  confirmCopy: {
    flex: 1,
    minWidth: 260,
    gap: 8,
  },
  confirmTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 42,
    lineHeight: 44,
  },
  confirmBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 23,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  nextPanel: {
    gap: spacing.md,
  },
  nextCopy: {
    gap: 6,
  },
  nextTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 34,
    lineHeight: 36,
  },
  nextBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
});
